import {Auth0Provider as Auth0, useAuth0} from '@auth0/auth0-react';
import {pickDefined} from '@sollapay/utils';
import {createContext, useCallback, useContext, useEffect, useState} from 'react';

import {logger} from '../../utils/logger.utils';
import {
  getAuthModeFromPath,
  getGuestAppUrl,
  getLoginContext,
  resolveAuthMode
} from '../utils/auth-mode.utils';
import {
  clearRememberedOrganization,
  getRememberedOrganization,
  rememberOrganization
} from '../utils/remembered-organization.utils';

import type {AuthMode} from '../utils/auth-mode.utils';
import type {LoginContext} from '../utils/login-params.utils';
import type {AppState, CacheLocation} from '@auth0/auth0-react';
import type {FC, PropsWithChildren, ReactNode} from 'react';

const log = logger.createLogger('Auth0Provider');

const AuthModeContext = createContext<AuthMode>('member');

export interface Auth0ProviderConfig {
  domain: string;
  clientId: string;
  audience: string;
  guestClientId?: string;
  /** How long a guest stays signed in after entering their code; unset leaves it to the Auth0 session. */
  guestMaxSessionSeconds?: number;
  cacheLocation?: Extract<CacheLocation, 'memory' | 'localstorage'>;
}

export interface Auth0ProviderProps extends PropsWithChildren {
  config: Auth0ProviderConfig;
  /** Wire this to the host app's router so a redirect (login or recovery) returns to `appState.returnTo` instead of the SDK default of dropping the user at `redirect_uri`. */
  onRedirectCallback?: (appState?: AppState) => void;
}

export const Auth0Provider: FC<Auth0ProviderProps> = ({config, onRedirectCallback, children}) => {
  const [mode] = useState(() => resolveMountedMode(config.guestClientId));
  const isGuest = mode === 'guest';

  return (
    <Auth0
      domain={config.domain}
      useRefreshTokens={true}
      // Memory-only tokens lose the refresh token on reload; restore the session via a hidden iframe instead of a redirect.
      useRefreshTokensFallback={true}
      cacheLocation={config.cacheLocation ?? 'memory'}
      clientId={isGuest && config.guestClientId ? config.guestClientId : config.clientId}
      authorizationParams={{
        // A guest returns under `/guest`, so the page that handles the login (or its error) loads as a guest.
        redirect_uri: isGuest ? getGuestAppUrl() : window.location.origin,
        audience: config.audience,
        ...pickDefined({
          organization: isGuest ? undefined : getRememberedOrganization(),
          // Auth0 refuses a silent sign-in once the code is older than this, so a reload asks for a new one.
          max_age: isGuest ? config.guestMaxSessionSeconds : undefined
        })
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {!isGuest && <OrganizationMemory />}
      <AuthModeProvider mode={mode}>{children}</AuthModeProvider>
    </Auth0>
  );
};

/** Sets the mounted auth mode; `Auth0Provider` renders it, and stories use it to render as a guest. */
export const AuthModeProvider: FC<{mode: AuthMode; children: ReactNode}> = ({mode, children}) => (
  <AuthModeContext.Provider value={mode}>{children}</AuthModeContext.Provider>
);

/** Whether this tab mounted as an outside guest. */
export function useIsGuestSession(): boolean {
  return useContext(AuthModeContext) === 'guest';
}

/** Login context for the mode this tab mounted with; another tab's stored mode can't change this tab's Auth0 client. */
export function useLoginContextGetter(): () => LoginContext {
  const mode = useContext(AuthModeContext);
  return useCallback(() => getLoginContext(mode), [mode]);
}

const OrganizationMemory: FC = () => {
  const {user} = useAuth0();
  const organizationId: unknown = user?.org_id;
  const isSignedIn = Boolean(user);

  useEffect(() => {
    if (!isSignedIn) return;
    if (typeof organizationId === 'string') rememberOrganization(organizationId);
    else clearRememberedOrganization();
  }, [isSignedIn, organizationId]);

  return null;
};

function resolveMountedMode(guestClientId: string | undefined): AuthMode {
  if (!guestClientId && getAuthModeFromPath() === 'guest') {
    log.error('A guest path was opened but no guest client id is configured');
  }
  return resolveAuthMode(guestClientId);
}
